import React from 'react';
import { prismicText } from 'helpers';
import { PrismicHTML, PrismicExactImage, PrismicFluidImage } from 'components/Prismic';
import { Container } from '../../UI/Container/Container';
import './TextGrid.scss';
import { Grid, GridRow, GridCol, colSize_t, offsetSize_t } from '../../UI/Grid';
import { PrismicRichTextFieldType, PrismicTitleFieldType, PrismicImageField, PrismicFluidImageField } from '../types';
import { SupportedPrismicSlicesEnum } from '../enums';

type TextGridProps = {
    slice: TextGridSlice;
};

type TextGridSlice = {
    type: SupportedPrismicSlicesEnum.text_grid;
    primary: {
        text_grid_number_of_columns: '2' | '3';
        grid_title: PrismicTitleFieldType;
    };
    fields: TextGridItem[];
};

type TextGridItem = {
    text_grid_title: PrismicTitleFieldType;
    text_grid_description: PrismicRichTextFieldType;
    text_grid_logo: PrismicImageField;
    text_grid_full_image: PrismicImageField;
    text_grid_full_imageSharp: PrismicFluidImageField;
};

// Do the conversion in column width
const getColSize = (nbrCol: string, hasTitle: boolean): colSize_t | undefined => {
    switch (nbrCol) {
        case '2':
            return hasTitle ? '4' : '6';
        case '3':
            return hasTitle ? '3' : '4';
        default:
            return;
    }
};

// Returns an offset when there is a title and the element is the first of the 2nd+ row.
// This allows the title have the full height of the grid
const calculateOffset = (i: number, nbrCol: string, hasTitle: boolean): offsetSize_t | undefined => {
    if (hasTitle && i !== 0) {
        const modulo: number = i % parseInt(nbrCol, 10);

        if (modulo === 0) {
            return getColSize(nbrCol, hasTitle);
        }
    }
};

const TextGridItem = ({
    item,
    colSize,
    offsetSize,
}: {
    item: TextGridItem;
    colSize: colSize_t | undefined;
    offsetSize: offsetSize_t | undefined;
}) => {
    return (
        <GridCol
            colSize={colSize}
            offsetSize={offsetSize}
            tabletColSize={'6'}
            tabletOffsetSize={'0'}
            mobileColSize={'12'}
            className="Bloc-text"
        >
            <div className="TextGrid-item">
                <PrismicFluidImage
                    className="TextGrid-item-fullimage"
                    image={item.text_grid_full_imageSharp}
                    fallback={item.text_grid_full_image}
                />
                <div className="TextGrid-item-header">
                    <PrismicExactImage className="u-mr-1" image={item.text_grid_logo} />
                    <PrismicHTML content={item.text_grid_title} />
                </div>
                <PrismicHTML content={item.text_grid_description} className="TextGrid-item-content" />
            </div>
        </GridCol>
    );
};

const TextGrid = ({ slice }: TextGridProps) => {
    const nbrCol = slice.primary.text_grid_number_of_columns;
    const hasTitle = !!prismicText(slice.primary.grid_title);
    const colSize = getColSize(nbrCol, hasTitle);
    return (
        <Container size="md">
            <Grid className="Text-bloc">
                <GridRow horizontalAlign={'h-start'} verticalAlign={'v-stretch'} className="Bloc-row">
                    {hasTitle && (
                        <GridCol colSize={colSize} tabletColSize={'12'} className="Col-title">
                            <PrismicHTML content={slice.primary.grid_title} className="TextGrid-title" />
                        </GridCol>
                    )}

                    {slice.fields.map((item, i: number) => (
                        <TextGridItem
                            key={i}
                            item={item}
                            colSize={colSize}
                            offsetSize={calculateOffset(i, nbrCol, hasTitle)}
                        />
                    ))}
                </GridRow>
            </Grid>
        </Container>
    );
};

export { TextGrid, TextGridProps, TextGridItem };
